import {
  useMutation, useQuery, useQueryClient, type QueryKey,
} from 'react-query';
import {
  resetReactQuery,
  TUseMutationContext,
  TUseMutationOptions,
  TUseQueryOptions,
} from '@uniqkey-frontend/shared-app';
import {
  GetPartnerByIdResponse,
  MeResponse,
  NoContentResult,
  UpdatePartnerRequest,
} from '@uniqkey-backend-partner/api-client';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import usePartnersAPI from '../../usePartnersAPI';
import { useUser } from '../../../contexts/UserContext';
import { REACT_QUERY_PARTNERS_KEY } from '../../tables/usePartnersTable';

export const REACT_QUERY_PARTNER_KEY = [ReactQueryKeyEnum.Partner];

interface IUseGetPartnerByIdParams {
  partnerId: string;
}

export const useGetPartnerById = (
  params: IUseGetPartnerByIdParams,
  options: TUseQueryOptions<GetPartnerByIdResponse> = {},
) => {
  const { partnerId } = params;
  const { getPartnerById } = usePartnersAPI();
  return useQuery<GetPartnerByIdResponse>(
    (REACT_QUERY_PARTNER_KEY as QueryKey[]).concat([partnerId]),
    ({ signal }) => getPartnerById(partnerId, { signal }),
    { notifyOnChangeProps: 'tracked', ...options },
  );
};

export const useGetParentPartner = (
  options: TUseQueryOptions<GetPartnerByIdResponse> = {},
) => {
  const { currentUser, isCurrentUserLoading } = useUser();
  const { getPartnerById } = usePartnersAPI();
  const { partnerId } = currentUser ?? {} as MeResponse;
  const { enabled = true, ...restOptions } = options;
  return useQuery<GetPartnerByIdResponse>(
    (REACT_QUERY_PARTNER_KEY as QueryKey[]).concat([partnerId]),
    ({ signal }) => getPartnerById(partnerId, { signal }),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !isCurrentUserLoading && enabled,
      ...restOptions,
    },
  );
};

interface IUseUpdatePartnerParams {
  partnerId: string;
  useOptimisticUpdates?: boolean;
}

export const useUpdatePartner = (
  params: IUseUpdatePartnerParams,
  options: TUseMutationOptions<
    NoContentResult,
    unknown,
    Omit<UpdatePartnerRequest, 'partnerId'>,
    TUseMutationContext<GetPartnerByIdResponse>
  > = {},
) => {
  const { partnerId, useOptimisticUpdates = false } = params;
  const queryClient = useQueryClient();
  const { updatePartner } = usePartnersAPI();
  const mutationKey = (REACT_QUERY_PARTNER_KEY as QueryKey[]).concat([partnerId]);
  return useMutation(
    mutationKey,
    (partner) => updatePartner(
      { ...partner, partnerId },
    ),
    {
      onMutate: async (newPartner) => {
        if (!useOptimisticUpdates) {
          return null;
        }
        await queryClient.cancelQueries(mutationKey);
        const previousValue = queryClient.getQueryData<
          GetPartnerByIdResponse
        >(mutationKey);
        queryClient.setQueryData<GetPartnerByIdResponse>(
          mutationKey,
          (oldPartner) => ({
            ...oldPartner,
            ...newPartner as GetPartnerByIdResponse,
          }),
        );
        return { previousValue: previousValue as GetPartnerByIdResponse };
      },
      onError: (err, partner, context) => {
        if (context?.previousValue) {
          queryClient.setQueryData<GetPartnerByIdResponse>(
            mutationKey,
            context.previousValue,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(mutationKey);
        resetReactQuery(queryClient, REACT_QUERY_PARTNERS_KEY);
      },
      ...options,
    },
  );
};
